/**
 * 타로 용어 사전 — 처음 보는 사람이 막히는 단어들을 풀어 쓴 단일 출처.
 * 화면(/glossary)과 카드 상세 페이지가 같은 정의를 참조한다.
 *
 * 용어 이름·설명을 화면에 하드코딩하지 말고 여기서 가져다 쓸 것 (CLAUDE.md §5).
 * `id`는 URL 앵커(#suit)로도 쓰이므로 바꾸면 링크가 깨진다.
 */
export interface GlossaryTerm {
  id: string;
  /** 표제어 */
  term: string;
  /** 원어·별칭 (있을 때만) */
  alias?: string;
  /** 한 줄 요약 */
  short: string;
  /** 본문 — 문단 단위 */
  body: string[];
}

export const GLOSSARY: readonly GlossaryTerm[] = [
  {
    id: 'arcana',
    term: '아르카나',
    alias: 'Arcana',
    short: '타로 78장을 나누는 큰 분류. 메이저 22장 + 마이너 56장.',
    body: [
      '아르카나는 라틴어로 "비밀"이라는 뜻이에요. 타로 한 벌(78장)은 메이저 아르카나 22장과 마이너 아르카나 56장으로 나뉩니다.',
      '메이저 아르카나는 인생의 큰 흐름과 전환점을 다뤄요. 바보(0번)에서 시작해 세계(21번)로 끝나는 한 사람의 성장 여정으로 읽기도 합니다. 리딩에서 메이저 카드가 많이 나오면 지금 상황이 그만큼 비중 있는 국면이라는 신호로 봅니다.',
      '마이너 아르카나는 일상에서 마주치는 구체적인 장면을 다룹니다. 네 가지 슈트로 나뉘고, 각 슈트마다 14장씩 있어요.',
    ],
  },
  {
    id: 'suit',
    term: '슈트',
    alias: 'Suit',
    short: '마이너 아르카나를 네 갈래로 나눈 무늬. 트럼프의 하트·스페이드 같은 개념.',
    body: [
      '슈트는 마이너 아르카나 56장을 주제별로 나눈 네 갈래예요. 트럼프 카드의 하트·다이아·클로버·스페이드와 같은 개념이라고 보시면 쉽습니다.',
      '완드(Wands)는 열정과 행동, 컵(Cups)은 감정과 관계, 소드(Swords)는 사고와 결단, 펜타클(Pentacles)은 물질과 현실을 다뤄요.',
      '그래서 어떤 슈트가 많이 나왔는지만 봐도 흐름이 읽힙니다. 컵이 여러 장이면 마음과 관계가 중심인 시기, 펜타클이 많으면 돈·일 같은 현실 문제가 중심인 시기로 볼 수 있어요.',
    ],
  },
  {
    id: 'rank',
    term: '랭크 · 코트 카드',
    alias: 'Rank / Court Card',
    short: '각 슈트 안의 번호(에이스~10)와 인물 카드(시종·기사·여왕·왕).',
    body: [
      '슈트마다 에이스부터 10까지 숫자 카드 10장과, 시종·기사·여왕·왕 네 장의 인물 카드가 있어 총 14장이 됩니다.',
      '숫자는 대체로 이야기의 진행 단계를 나타내요. 에이스는 시작과 잠재력, 중간 숫자는 전개와 시련, 10은 완결을 뜻하는 식입니다.',
      '인물 카드(코트 카드)는 사람이나 태도를 가리키는 경우가 많아요. 실제 주변 인물일 수도 있고, 내가 지금 취하고 있는 자세로 읽기도 합니다.',
    ],
  },
  {
    id: 'upright-reversed',
    term: '정방향 · 역방향',
    alias: 'Upright / Reversed',
    short: '카드가 똑바로 놓였는지, 뒤집혀 놓였는지. 같은 카드도 의미가 달라진다.',
    body: [
      '카드를 펼쳤을 때 그림이 똑바로 서 있으면 정방향, 위아래가 뒤집혀 있으면 역방향입니다.',
      '역방향이 곧 "나쁜 결과"라는 뜻은 아니에요. 같은 에너지가 아직 드러나지 않았거나, 밖이 아니라 안쪽으로 향하고 있거나, 지나쳐서 과해진 상태로 읽는 경우가 많습니다.',
      '예를 들어 힘 카드가 정방향이면 용기와 인내를, 역방향이면 자기 의심이나 충동을 말할 수 있어요. 어느 쪽이든 "지금 이 주제를 살펴보라"는 신호인 건 같습니다.',
    ],
  },
  {
    id: 'spread',
    term: '스프레드',
    alias: 'Spread',
    short: '카드를 몇 장, 어떤 자리에 놓을지 정한 배열법.',
    body: [
      '스프레드는 카드를 펼치는 방식이에요. 몇 장을 뽑고 각 자리가 무엇을 뜻하는지 미리 정해둔 틀입니다.',
      '가장 단순한 건 한 장만 뽑는 원 카드예요. 오늘 하루처럼 가벼운 질문에 잘 맞습니다. 3장 스프레드는 과거·현재·미래를 봐서 흐름을 읽을 때 쓰고, 관계를 볼 때는 두 사람의 마음과 과제를 나눠 보는 5장 배열을 씁니다.',
      '켈틱 크로스는 10장을 쓰는 전통적인 배열로, 상황·장애물·무의식·주변 환경까지 폭넓게 봅니다.',
    ],
  },
  {
    id: 'position',
    term: '포지션 (자리)',
    alias: 'Position',
    short: '스프레드에서 카드가 놓인 각 자리. 같은 카드도 자리에 따라 뜻이 달라진다.',
    body: [
      '포지션은 스프레드 안에서 카드가 놓이는 자리예요. "과거", "상대의 마음", "넘어야 할 것"처럼 자리마다 질문이 정해져 있습니다.',
      '해석에서 자리가 중요한 이유는, 같은 카드라도 어디에 놓였느냐에 따라 답이 달라지기 때문이에요. 탑 카드가 "과거"에 있으면 이미 지나간 충격이지만, "가까운 미래"에 있으면 앞으로의 변화를 예고합니다.',
    ],
  },
  {
    id: 'shuffle',
    term: '셔플 · 컷',
    alias: 'Shuffle / Cut',
    short: '카드를 섞고 나누는 과정. 질문에 집중하는 시간이기도 하다.',
    body: [
      '셔플은 카드를 섞는 것, 컷은 섞은 덱을 나누는 것을 말해요.',
      '기술적으로는 무작위를 만드는 과정이지만, 실제 상담에서는 질문에 집중하며 마음을 정리하는 시간으로 여깁니다. 무엇이 궁금한지 또렷할수록 해석도 또렷해져요.',
    ],
  },
  {
    id: 'rws',
    term: '라이더-웨이트 덱',
    alias: 'Rider–Waite–Smith',
    short: '가장 널리 쓰이는 표준 타로 덱. 이 서비스가 쓰는 그림도 이것.',
    body: [
      '1909년 영국에서 나온 덱으로, 아서 웨이트가 기획하고 파멜라 콜먼 스미스가 그림을 그렸습니다. 오늘날 가장 널리 쓰이는 표준이라 대부분의 타로 해설이 이 덱을 기준으로 합니다.',
      '마이너 아르카나까지 모든 카드에 장면이 그려져 있어 그림만으로도 이야기를 읽을 수 있는 게 특징이에요. 저작권이 만료된 퍼블릭 도메인이라 자유롭게 쓸 수 있습니다.',
      '이 서비스가 쓰는 카드 그림도 이 덱이에요. Illustrated by Pamela Colman Smith — Public Domain.',
    ],
  },
] as const;

export const findGlossaryTerm = (id: string): GlossaryTerm | undefined =>
  GLOSSARY.find((t) => t.id === id);
